/**
 * SOS — всегда видимая спокойная кнопка (City Resilience Hack, 24.09.2026):
 * НЕ мигает и не пищит (это AlarmBar, для боевой тревоги) — сама пилюля лишь
 * открывает лист с тремя действиями: «Укрытие», «Выход» (только на площадке)
 * и «112». Кнопки никого не оповещают — только показывают дорогу самому
 * человеку (см. дисклеймер в разметке).
 *
 * На площадке (venueId задан) «Укрытие»/«Выход» ставят синтетический кадр
 * self-frame.ts — та же машинерия ведения, что у живой тревоги, без подписи
 * и без чужого оповещения. На главной (venueId нет) «Укрытие» показывает
 * список ближайших публичных укрытий (public-shelters-panel.tsx) — как в
 * баннере городской тревоги (directory-alert.tsx), кнопкой по требованию.
 *
 * Во время любого активного аварийного кадра пилюля прячется — аварийная
 * панель уже на экране, дублировать нечего.
 */
import { tr } from '@app/i18n'
import { useState } from 'preact/hooks'
import { buildSelfFrame } from '../emergency/self-frame'
import {
  alertCheckedAt,
  alertOffline,
  emergencyDrill,
  emergencyFrame,
  emergencyHazard,
  langTick,
} from '../state'
import { PublicSheltersPanel } from './public-shelters-panel'

function goSelf(venueId: string, mode: 0 | 1): void {
  emergencyFrame.value = buildSelfFrame(venueId, mode)
  emergencyHazard.value = null
  emergencyDrill.value = false
}

function StatusLine() {
  if (alertOffline.value) return <p class="sos-status">{tr('sos.status_offline')}</p>
  const checked = alertCheckedAt.value
  const n = checked ? Math.max(0, Math.round((Date.now() - checked) / 60_000)) : null
  return (
    <p class="sos-status">
      {n === null ? tr('sos.status_unknown') : tr('sos.status_checked', { n: String(n) })}
    </p>
  )
}

export function SosBar({ venueId = null }: { venueId?: string | null }) {
  langTick.value
  const [open, setOpen] = useState(false)
  const [showShelters, setShowShelters] = useState(false)

  if (emergencyFrame.value !== null) return null

  const close = (): void => {
    setOpen(false)
    setShowShelters(false)
  }

  return (
    <>
      <button
        type="button"
        class="sos-pill"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={tr('sos.button')}
      >
        {tr('sos.button')}
      </button>
      {open && (
        <div
          class="sos-sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={tr('sos.sheet_title')}
        >
          <div class="sos-sheet">
            <div class="sos-sheet-head">
              <h2 class="sos-sheet-title">{tr('sos.sheet_title')}</h2>
              <button
                type="button"
                class="sos-close"
                aria-label={tr('common.close')}
                onClick={close}
              >
                ✕
              </button>
            </div>
            <StatusLine />
            {showShelters ? (
              <PublicSheltersPanel />
            ) : (
              <div class="sos-actions">
                <button
                  type="button"
                  class="sos-action sos-action-shelter"
                  onClick={() => (venueId ? goSelf(venueId, 1) : setShowShelters(true))}
                >
                  {tr('sos.shelter')}
                </button>
                {venueId && (
                  <button
                    type="button"
                    class="sos-action sos-action-exit"
                    onClick={() => goSelf(venueId, 0)}
                  >
                    {tr('sos.exit')}
                  </button>
                )}
                <a class="sos-action sos-action-call" href="tel:112">
                  {tr('sos.call112')}
                </a>
              </div>
            )}
            <p class="sos-disclaimer">{tr('sos.disclaimer')}</p>
          </div>
        </div>
      )}
    </>
  )
}
